// Find records (2026-11-02): one search over signed reports, people's documents and the SmartSearch archive, by name / date of
// birth / ID, organization, dates and kind, without opening a person or organization first. Export the list to Excel (with
// each report's main results), or tick rows and make one PDF of them. Download only. The filters live in the address bar.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { post } from '../lib/api.ts';
import { useData } from '../lib/useData.ts';
import { OrgPicker } from './Reports.tsx';

type Kind = 'report' | 'document' | 'archive';
interface Row {
  kind: Kind; id: string; participantId: string | null; lastName: string | null; firstName: string | null; dob: string | null;
  organization: string | null; date: string | null; yearOnly: boolean; type: string; title: string | null; label: string;
}
interface Bundle { id: string; status: 'queued' | 'running' | 'done' | 'failed'; title: string | null; error: string | null; people: number; items: number;
  pageCount: number | null; createdAt: string }

const KIND_LABEL: Record<Kind, string> = { report: 'Reports', document: 'Documents', archive: 'Old archive (SmartSearch)' };
const DOC_TYPES: [string, string][] = [
  ['lab_panel', 'Lab results'], ['ekg', 'EKG'], ['pft', 'Spirometry / PFT'], ['clearance', 'Clearance form'], ['test_result', 'Test result'],
  ['medical_history', 'Medical history'], ['drug_screen', 'Drug screen'], ['intake_form', 'Intake form (scan)'], ['outside_record', 'Outside record'],
  ['clearance_letter', 'Clearance letter'], ['follow_up', 'Follow-up'], ['authorization', 'Authorization / consent'], ['other', 'Other'],
];
const PAGE = 100;
const MAX_PDF = 200;
const usDate = (iso: string | null, yearOnly = false) => (!iso ? '—' : yearOnly ? iso.slice(0, 4) : `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}`);
const keyOf = (r: { kind: Kind; id: string }) => `${r.kind}:${r.id}`;
const openUrl = (r: Row) => r.kind === 'report' ? `/assessments/${r.id}/report` : r.kind === 'document' ? `/api/documents/${r.id}/file` : `/api/archive/${r.id}/file`;
const itemOf = (k: string) => {
  const [kind, id] = k.split(':') as [Kind, string];
  return kind === 'report' ? { kind, assessmentId: id } : kind === 'document' ? { kind, documentId: id } : { kind, archiveId: id };
};

export function FindRecords() {
  const [sp, setSp] = useSearchParams();
  const [form, setForm] = useState(() => ({
    q: sp.get('q') ?? '', org: (sp.get('org') ?? '').split(',').filter(Boolean), from: sp.get('from') ?? '', to: sp.get('to') ?? '',
    kinds: (sp.get('kinds') ?? 'report,document,archive').split(',').filter(Boolean) as Kind[], docTypes: (sp.get('docTypes') ?? '').split(',').filter(Boolean),
  }));
  const [offset, setOffset] = useState(0);
  const [picked, setPicked] = useState<Map<string, Row>>(new Map());
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const query = sp.toString();
  useEffect(() => { setOffset(0); }, [query]);
  const results = useData<{ total: number; rows: Row[] }>(query ? `/api/find?${query}&limit=${PAGE}&offset=${offset}` : null);
  const bundles = useData<Bundle[]>('/api/find/bundles');
  const working = (bundles.data ?? []).some((b) => b.status === 'queued' || b.status === 'running');
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void bundles.reload(), 3000);
    return () => clearInterval(t);
  }, [working, bundles.reload]);

  const search = (e: FormEvent) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (form.q.trim()) p.set('q', form.q.trim());
    if (form.org.length) p.set('org', form.org.join(','));
    if (form.from) p.set('from', form.from);
    if (form.to) p.set('to', form.to);
    if (form.kinds.length && form.kinds.length < 3) p.set('kinds', form.kinds.join(','));
    if (form.kinds.includes('document') && form.docTypes.length) p.set('docTypes', form.docTypes.join(','));
    setSp(p);
  };
  const toggleKind = (k: Kind) => setForm({ ...form, kinds: form.kinds.includes(k) ? form.kinds.filter((x) => x !== k) : [...form.kinds, k] });
  const rows = results.data?.rows ?? [];
  const allOnPage = rows.length > 0 && rows.every((r) => picked.has(keyOf(r)));
  const tickPage = (on: boolean) => {
    const n = new Map(picked);
    for (const r of rows) { if (on) { if (n.size < MAX_PDF || n.has(keyOf(r))) n.set(keyOf(r), r); } else n.delete(keyOf(r)); }
    setPicked(n);
  };
  const tick = (r: Row, on: boolean) => { const n = new Map(picked); if (on) n.set(keyOf(r), r); else n.delete(keyOf(r)); setPicked(n); };
  const people = useMemo(() => new Set([...picked.values()].map((r) => r.participantId ?? `${r.lastName}|${r.firstName}|${r.dob}`)).size, [picked]);

  const makePdf = async () => {
    setBusy(true); setErr(null);
    try {
      await post('/api/find/bundles', { items: [...picked.keys()].map(itemOf), title: title.trim() || undefined });
      setPicked(new Map()); setTitle('');
      void bundles.reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="spread"><h1>Find records</h1><Link to="/help/find-records" className="small">How this works</Link></div>
      <p className="muted">Reports, documents and old SmartSearch files in one list, without opening the person or organization first.
        Type a name, date of birth or ID, and/or choose organizations and dates.</p>
      <form className="card" onSubmit={search}>
        <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: '1 1 260px' }}><label>Name, date of birth or ID</label>
            <input value={form.q} onChange={(e) => setForm({ ...form, q: e.target.value })} placeholder="Smith John · 03/07/1988 · 4471" autoFocus /></div>
          <div className="field"><label>From</label><input type="date" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} /></div>
          <div className="field"><label>To</label><input type="date" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} /></div>
        </div>
        <div className="field"><label>Organizations</label><OrgPicker value={form.org} onChange={(org) => setForm({ ...form, org })} /></div>
        <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
          {(Object.keys(KIND_LABEL) as Kind[]).map((k) => (
            <label key={k}><input type="checkbox" checked={form.kinds.includes(k)} onChange={() => toggleKind(k)} />{KIND_LABEL[k]}</label>))}
        </div>
        {form.kinds.includes('document') && (
          <details style={{ marginTop: '0.5rem' }} open={form.docTypes.length > 0}>
            <summary className="small">Only some kinds of document{form.docTypes.length ? ` (${form.docTypes.length} chosen)` : ''}</summary>
            <div className="chips" style={{ marginTop: '0.4rem' }}>{DOC_TYPES.map(([k, l]) => (
              <label key={k} className="small"><input type="checkbox" checked={form.docTypes.includes(k)}
                onChange={(e) => setForm({ ...form, docTypes: e.target.checked ? [...form.docTypes, k] : form.docTypes.filter((x) => x !== k) })} />{l}</label>))}</div>
          </details>)}
        <div className="actions" style={{ marginTop: '0.75rem' }}>
          <button type="submit">Search</button>
          {query && <a className="btn secondary" href={`/api/find/export.xlsx?${query}`}>Export to Excel</a>}
        </div>
      </form>

      <ErrorBox error={results.error} />
      {results.data && (
        <div className="card">
          <div className="spread">
            <strong>{results.data.total.toLocaleString()} record{results.data.total === 1 ? '' : 's'}</strong>
            {results.data.total > PAGE && <span className="actions">
              <button className="secondary small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>‹ Previous</button>
              <span className="small muted">{offset + 1}–{Math.min(offset + PAGE, results.data.total)}</span>
              <button className="secondary small" disabled={offset + PAGE >= results.data.total} onClick={() => setOffset(offset + PAGE)}>Next ›</button>
            </span>}
          </div>
          {results.data.total > 5000 && <p className="small muted">Excel takes the first 5,000 rows; narrow the search for the rest.</p>}
          {rows.length > 0 && (
            <table className="small">
              <thead><tr>
                <th style={{ width: '2rem' }}><input type="checkbox" aria-label="Tick every row on this page" checked={allOnPage} onChange={(e) => tickPage(e.target.checked)} /></th>
                <th>Date</th><th>Person</th><th>Date of birth</th><th>Organization</th><th>Record</th><th></th>
              </tr></thead>
              <tbody>{rows.map((r) => {
                const name = [r.lastName, r.firstName].filter(Boolean).join(', ') || '—';
                return (
                  <tr key={keyOf(r)}>
                    <td><input type="checkbox" aria-label={`Tick ${r.label} for ${name}`} checked={picked.has(keyOf(r))}
                      disabled={!picked.has(keyOf(r)) && picked.size >= MAX_PDF} onChange={(e) => tick(r, e.target.checked)} /></td>
                    <td>{usDate(r.date, r.yearOnly)}</td>
                    <td>{r.participantId ? <Link to={`/participants/${r.participantId}`}>{name}</Link> : <span title="Not linked to a person">{name}</span>}</td>
                    <td>{usDate(r.dob)}</td>
                    <td>{r.organization ?? '—'}</td>
                    <td>{r.label}</td>
                    <td><a href={openUrl(r)} target="_blank" rel="noopener">Open</a></td>
                  </tr>);
              })}</tbody>
            </table>)}
        </div>)}

      {picked.size > 0 && (
        <div className="card" style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
          <div className="row" style={{ gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div><strong>{picked.size} ticked</strong> <span className="muted small">({people} {people === 1 ? 'person' : 'people'}{picked.size >= MAX_PDF ? `; ${MAX_PDF} is the most in one PDF` : ''})</span></div>
            <div className="field" style={{ flex: '1 1 220px', margin: 0 }}><label>Title on the cover (optional)</label>
              <input value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Greer FD records request" /></div>
            <button disabled={busy} onClick={() => void makePdf()}>{busy ? 'Starting…' : 'Make one PDF'}</button>
            <button className="secondary" onClick={() => setPicked(new Map())}>Clear ticks</button>
          </div>
          <ErrorBox error={err} />
        </div>)}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Your PDFs</h2>
        <ErrorBox error={bundles.error} />
        {!bundles.data?.length ? <p className="muted small" style={{ margin: 0 }}>Tick rows above and choose Make one PDF. They appear here to open or download.</p> : (
          <table className="small">
            <thead><tr><th>Made</th><th>Title</th><th>Contents</th><th></th></tr></thead>
            <tbody>{bundles.data.map((b) => (
              <tr key={b.id}>
                <td>{new Date(b.createdAt).toLocaleString()}</td>
                <td>{b.title ?? '—'}</td>
                <td>{b.items} item{b.items === 1 ? '' : 's'} · {b.people} {b.people === 1 ? 'person' : 'people'}{b.pageCount ? ` · ${b.pageCount} pages` : ''}</td>
                <td>{b.status === 'done'
                  ? <span className="actions"><a className="btn" href={`/api/find/bundles/${b.id}/pdf`} target="_blank" rel="noopener">Open</a>
                      <a className="btn secondary" href={`/api/find/bundles/${b.id}/pdf?download=1`}>Download</a></span>
                  : b.status === 'failed' ? <span className="small" style={{ color: '#a00' }} title={b.error ?? ''}>Failed: make it again</span>
                  : <span className="badge draft">{b.status === 'running' ? 'Making PDF…' : 'Queued…'}</span>}</td>
              </tr>))}</tbody>
          </table>)}
      </div>
    </div>
  );
}
